/**
 * SettingsPanel
 *
 * アプリ設定モーダル。
 *
 * 提供する項目（要件 6.4 設定パネル）：
 *   1. APIキー入力/変更（→ ApiKeyModal を開くトリガー）
 *   2. 認識間隔調整（2〜10秒スライダー）
 *   3. 履歴クリア
 *   4. バージョン情報
 */

import type { ChangeEvent } from 'react';

import { maskApiKey, SCAN_INTERVAL_MAX_MS, SCAN_INTERVAL_MIN_MS } from '../lib/storage';

import type { UserSettings } from '../types';

import styles from './SettingsPanel.module.css';

/** スライダーのステップ（0.5秒刻み） */
const STEP_MS = 500;

export interface SettingsPanelProps {
  settings: UserSettings;
  onUpdateSettings: (patch: Partial<UserSettings>) => void;
  /** 現在のAPIキー（マスク表示用、未設定時は null） */
  currentApiKey: string | null;
  onChangeApiKey: () => void;
  onClearHistory: () => void;
  /** 履歴件数（0なら CLEAR ボタンを無効化） */
  historyCount: number;
  appVersion: string;
  onClose: () => void;
}

export function SettingsPanel({
  settings,
  onUpdateSettings,
  currentApiKey,
  onChangeApiKey,
  onClearHistory,
  historyCount,
  appVersion,
  onClose,
}: SettingsPanelProps) {
  const handleIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const ms = Number(event.target.value);
    onUpdateSettings({ scanIntervalMs: ms });
  };

  const handleClearHistory = () => {
    if (historyCount === 0) return;
    if (window.confirm('履歴をすべて削除しますか？')) {
      onClearHistory();
    }
  };

  // ms → 秒（1.0刻みで端数だけ表示）
  const intervalSec = (settings.scanIntervalMs / 1000).toFixed(1);

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="settings-title">
      <div className={styles.dialog}>
        <div className={styles.header}>
          <h2 id="settings-title" className={styles.title}>SETTINGS</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="設定パネルを閉じる"
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          {/* APIキー */}
          <section className={styles.section}>
            <span className={styles.sectionLabel}>API KEY</span>
            <div className={styles.row}>
              <span className={styles.sectionValue}>
                {currentApiKey ? maskApiKey(currentApiKey) : '未設定'}
              </span>
              <button type="button" className={styles.button} onClick={onChangeApiKey}>
                CHANGE
              </button>
            </div>
          </section>

          {/* 認識間隔 */}
          <section className={styles.section}>
            <div className={styles.row}>
              <span className={styles.sectionLabel}>SCAN INTERVAL</span>
              <span className={styles.sectionValue}>{intervalSec} sec</span>
            </div>
            <input
              type="range"
              className={styles.slider}
              min={SCAN_INTERVAL_MIN_MS}
              max={SCAN_INTERVAL_MAX_MS}
              step={STEP_MS}
              value={settings.scanIntervalMs}
              onChange={handleIntervalChange}
              aria-label="認識間隔（秒）"
            />
            <div className={styles.sliderRange}>
              <span>{(SCAN_INTERVAL_MIN_MS / 1000).toFixed(0)}s</span>
              <span>{(SCAN_INTERVAL_MAX_MS / 1000).toFixed(0)}s</span>
            </div>
          </section>

          {/* 履歴クリア */}
          <section className={styles.section}>
            <span className={styles.sectionLabel}>HISTORY</span>
            <div className={styles.row}>
              <span className={styles.sectionValue}>
                {historyCount} / 10 件保存中
              </span>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonDanger}`}
                onClick={handleClearHistory}
                disabled={historyCount === 0}
              >
                CLEAR
              </button>
            </div>
          </section>

          {/* バージョン情報 */}
          <section className={styles.section}>
            <span className={styles.sectionLabel}>VERSION</span>
            <span className={styles.version}>ScouterLens v{appVersion}</span>
          </section>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.button} onClick={onClose}>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}
